import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { duocVaoCong, phamViCuaRoute } from '../../lib/pham-vi-route';
import { daCoTienTo, ghep, laDungChung } from '../../lib/duong-dan-cong';
import { useAuthStore } from '../../stores/auth';
import { usePhamVi } from '../../hooks/usePhamVi';
import { SaiCongView } from '../SaiCongView';

/**
 * Cổng phạm vi: chặn nhánh URL không thuộc về người này.
 *
 * Lọc thanh điều hướng là chưa đủ — người dùng còn gõ URL, còn giữ dấu trang
 * từ tài khoản cũ, và còn bấm liên kết trong email. Guard này là chốt chặn
 * thật; thanh điều hướng chỉ là tiện ích.
 *
 * Luật **không đối xứng**: nhân sự nền tảng mở được `/client/...` để xem giao
 * diện tenant, nhưng tài khoản tenant không mở được `/admin/...`. Xem
 * `duocVaoCong`.
 *
 * Đứng **trước** `PermissionGuard`: sai cổng là lý do mạnh hơn và cần câu trả
 * lời khác. Nói "không đủ quyền" với một tenant admin đang gõ `/admin/tenant`
 * sẽ khiến họ đi xin quyền cho một màn mà cổng của họ không bao giờ có.
 *
 * ⚠️ Đây là **điều hướng phía giao diện**, không phải kiểm soát truy cập.
 * Chặn thật nằm ở Gateway: nhóm route nền tảng chỉ nhận token của
 * `platform-admin` (ADR-0007).
 */
export function PhamViGuard({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const phamViNguoiDung = usePhamVi();
  const { pathname, search } = useLocation();

  // Chưa đăng nhập: `AuthGate` lo chuyển hướng, đừng chen vào giữa.
  if (!user) return <>{children}</>;

  if (laDungChung(pathname)) return <>{children}</>;

  /*
   * Đường dẫn chưa mang tiền tố: đưa về đúng nhánh thay vì ném ra màn lỗi.
   * Hai nguồn sinh ra chúng — dấu trang cũ và liên kết trong email (chặn ở
   * đây là làm gãy mọi liên kết có từ trước khi tách nhánh), và `/` trần, cửa
   * vào của ứng dụng: `ghep('/', phamVi)` cho ra gốc nhánh của người này.
   *
   * Đây là **nơi duy nhất** quyết định `/` đi đâu. Có lúc việc đó nằm ở một
   * component `TrangGoc` riêng; hai nơi cùng làm một việc thì sớm muộn lệch
   * nhau.
   */
  if (!daCoTienTo(pathname)) {
    return <Navigate to={`${ghep(pathname, phamViNguoiDung)}${search}`} replace />;
  }

  const phamViRoute = phamViCuaRoute(pathname);
  if (phamViRoute === undefined) return <>{children}</>;
  if (duocVaoCong(phamViNguoiDung, phamViRoute)) return <>{children}</>;

  return <SaiCongView />;
}
